import React, { Fragment } from 'react';
import type { Folder } from '@prisma/client';
import { Button } from '~/components/utils/buttons';
import FAIcon from '~/components/utils/FAIcons/FAIcon';
import { useDocumentsContext } from '~/react_context/DocumentsContext';
import type { ButtonProps } from '~/components/utils/UtilityProps';
import { dropTargetClass, useAttachmentDrop } from './useAttachmentDrop';

type FolderBreadcrumbsProps = {
	topLevelLabel?: string;
	folders: Folder[];
	currentFolderId: number | null;
};

type FolderBreadcrumbsWithoutRouterProps = {
	currentFolderId: number | null;
	folders: Folder[];
	setCurrentFolderId: (id: number | null) => void;
	topLevelLabel?: string;
};

export const FolderBreadcrumbsWithoutRouter = ({
	currentFolderId,
	folders,
	setCurrentFolderId,
	topLevelLabel = 'Attachments',
}: FolderBreadcrumbsWithoutRouterProps) => {
	let id: number | null = currentFolderId;
	const displayFolders: Folder[] = [];
	//Little wonky but we don't expect to go more than 2-3 levels deep
	while (!!id) {
		const folder = folders.find((f) => f.id == id);
		if (!folder) break;
		displayFolders.unshift(folder);
		id = folder.parentFolderId;
	}
	return (
		<div className="flex px-4 items-center space-x-2 text-xl font-bold">
			<FAIcon icon="folder" className="pr-2" />
			<Button className="text-primary" custom onClick={() => setCurrentFolderId(null)}>
				{topLevelLabel}
			</Button>
			{displayFolders.map((folder) => (
				<Fragment key={folder.id}>
					<span>/</span>
					<Button className="text-primary" custom onClick={() => setCurrentFolderId(folder.id)}>
						{folder.name}
					</Button>
				</Fragment>
			))}
		</div>
	);
};

const DroppableCrumb = ({ children, folderId, link }: { children: React.ReactNode; folderId: number | null; link: ButtonProps['link'] }) => {
	const [dropState, dropRef] = useAttachmentDrop(folderId);

	return (
		<span
			className={`rounded-md px-1 ${dropTargetClass(dropState)}`}
			ref={(node) => {
				dropRef(node);
			}}
		>
			<Button className="text-primary" custom link={link}>
				{children}
			</Button>
		</span>
	);
};

const FolderBreadcrumbs = ({ topLevelLabel = 'Attachments', folders, currentFolderId }: FolderBreadcrumbsProps) => {
	const { folderRoute } = useDocumentsContext();
	let id: number | null = currentFolderId;
	const displayFolders: Folder[] = [];
	//Little wonky but we don't expect to go more than 2-3 levels deep
	while (!!id) {
		const folder = folders.find((f) => f.id == id);
		if (!folder) break;
		displayFolders.unshift(folder);
		id = folder.parentFolderId;
	}
	return (
		<div className="flex px-4 items-center space-x-2 text-xl font-bold">
			<FAIcon icon="folder" className="pr-2" />
			<DroppableCrumb folderId={null} link={folderRoute(null)}>
				{topLevelLabel}
			</DroppableCrumb>
			{displayFolders.map((folder) => (
				<Fragment key={folder.id}>
					<span>/</span>
					<DroppableCrumb folderId={folder.id} link={folderRoute(folder.id)}>
						{folder.name}
					</DroppableCrumb>
				</Fragment>
			))}
		</div>
	);
};

export default FolderBreadcrumbs;
